import { useState, useEffect } from "react";
import { auth, db } from "../firebase";
import {
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithPopup,
} from "firebase/auth";
import { FcGoogle } from "react-icons/fc";
import { doc, setDoc, getDoc } from "firebase/firestore";

const RegisterForm = ({ onSwitchToLogin, onClose, affiliateId }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // State for affiliate info from link
  const [affiliateMeta, setAffiliateMeta] = useState(null);

  // On mount, if affiliateId, load link details
  useEffect(() => {
    async function fetchAffiliateMeta() {
      if (!affiliateId) return;
      try {
        const ref = doc(db, "affiliate_links", affiliateId);
        const snap = await getDoc(ref);
        if (snap.exists()) setAffiliateMeta(snap.data());
      } catch (err) {
        // fail silently
      }
    }
    fetchAffiliateMeta();
  }, [affiliateId]);

  const saveUserAffiliate = async (user) => {
    if (!user || !affiliateId || !affiliateMeta) return;
    // Save all info for attribution!
    await setDoc(
      doc(db, "users", user.uid),
      {
        affiliateId: affiliateMeta.userId, // affiliate user UID
        affiliateLinkId: affiliateId, // this link's ID
        affiliateProgramType:
          affiliateMeta.program === "Revenue Sharing" ? "revenue" : "turnover",
        affiliateLinkType: affiliateMeta.linkType,
        affiliateComment: affiliateMeta.comment || "",
      },
      { merge: true }
    );
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const cred = await createUserWithEmailAndPassword(auth, email, password);
      await saveUserAffiliate(cred.user);
      onClose();
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  const handleGoogleRegister = async () => {
    setLoading(true);
    setError("");
    try {
      const provider = new GoogleAuthProvider();
      const cred = await signInWithPopup(auth, provider);
      await saveUserAffiliate(cred.user);
      onClose();
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  return (
    <form onSubmit={handleRegister} className="space-y-4">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-xl font-semibold">Sign Up</h3>
      </div>
      <div>
        <input
          type="email"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full px-4 py-2 bg-slate-800 text-white rounded focus:outline-none focus:ring"
          required
        />
      </div>
      <div>
        <input
          type="password"
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="w-full px-4 py-2 bg-slate-800 text-white rounded focus:outline-none focus:ring"
          required
        />
      </div>
      {error && <p className="text-red-500 text-sm">{error}</p>}

      {/* Show Affiliate Attribution UI (optional, can hide in production) */}

      <div className="text-left text-sm text-gray-300">
        <label className="inline-flex items-start gap-2">
          <input type="checkbox" className="mt-1" required />
          <span>
            I agree to the{" "}
            <span className="text-green-400">User Agreement</span> & confirm I
            am at least 18 years old.
          </span>
        </label>
        <br />
        <label className="inline-flex items-start gap-2 mt-2">
          <input type="checkbox" className="mt-1" />
          <span>I agree to receive marketing promotions.</span>
        </label>
      </div>
      <button
        type="submit"
        disabled={loading}
        className="w-full py-2 rounded bg-green-500 text-white font-semibold hover:bg-green-600"
      >
        {loading ? "Registering..." : "Sign Up"}
      </button>
      <button
        type="button"
        className="w-full mb-3 flex items-center justify-center gap-2 py-2 rounded bg-white text-black font-semibold border hover:bg-gray-100"
        onClick={handleGoogleRegister}
        disabled={loading}
      >
        <FcGoogle size={24} />
        Sign Up with Google
      </button>
      <p className="text-sm text-center text-gray-400">
        Already have an account?{" "}
        <span
          className="text-green-500 cursor-pointer hover:underline"
          onClick={onSwitchToLogin}
        >
          Sign In
        </span>
      </p>
    </form>
  );
};

export default RegisterForm;
